import { ColumnsGrid } from '../Grid';
import { LoadingContacts } from '../LoadingContactCard';
import { SmallPaddedScrollable } from '../Scrollable';

import { OrgContactCardPure } from './OrgContactCardPure';
import type { OrgContactItem, Stage } from './types';

export type OrgInteractSectionPureProps = {
  contacts: OrgContactItem[] | undefined;
  contactsLoading: boolean;
  stages: Stage[] | undefined;
  stagesLoading: boolean;
  /** The contact whose stage is currently saving (renders that card's spinner). */
  stageUpdatingContactId?: string | null;
  /**
   * Whether a card is the active/open one. The dashboard implements this via the
   * router (`query.id === joinIds(...)`); the extension omits it. Kept as a
   * predicate so the shared component stays free of URL semantics.
   */
  isContactActive?: (contactId: string, cadenceId: string | undefined) => boolean;
  /** Whether a card is selected (dashboard multi-select). Omitted on the extension. */
  isContactChecked?: (contactId: string, cadenceId: string | undefined) => boolean;
  onContactClick: (args: { contactId: string; cadenceId?: string }) => void;
  onContactStageChange: (contactId: string, stageId: string | null) => void;
};

/**
 * Pure port of the dashboard's app-local OrgInteractSection: the list of
 * contacts that belong to an org. All router/mutation coupling is lifted to
 * props (click, stage-change, active/checked predicates), and an explicit empty
 * state is rendered (the app version rendered nothing).
 */
export const OrgInteractSectionPure = ({
  contacts,
  contactsLoading,
  stages,
  stagesLoading,
  stageUpdatingContactId,
  isContactActive,
  isContactChecked,
  onContactClick,
  onContactStageChange,
}: OrgInteractSectionPureProps) => (
  <SmallPaddedScrollable>
    <ColumnsGrid gap={6} columns={1} columnMinWidth={0}>
      {contactsLoading && <LoadingContacts />}
      {!contactsLoading && (contacts?.length ?? 0) === 0 && (
        <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
          No contacts in this organization
        </div>
      )}
      {contacts?.map((item) => (
        <OrgContactCardPure
          key={item.contact.id}
          item={item}
          stages={stages ?? []}
          stagesLoading={stagesLoading}
          stageUpdating={stageUpdatingContactId === item.contact.id}
          isActive={isContactActive?.(item.contact.id, item.activeCadence?.cadence.id) ?? false}
          isChecked={isContactChecked?.(item.contact.id, item.activeCadence?.cadence.id) ?? false}
          onClick={() => onContactClick({ contactId: item.contact.id, cadenceId: item.activeCadence?.cadence.id })}
          onStageChange={(stageId) => onContactStageChange(item.contact.id, stageId)}
        />
      ))}
    </ColumnsGrid>
  </SmallPaddedScrollable>
);
